import type { ConversationListItem } from "@esi/shared";
import { FolderClosed, MessageCircle, Search } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Snippet, useDebounced } from "../../conversation/ConversationList.tsx";
import { useConversationPage } from "../../conversation/queries.ts";
import { conversationPath, navigate } from "../../router.tsx";

/** Recent conversations shown before typing, and results per search. */
const RECENT = 8;
const RESULTS = 20;

/** Whether a key event comes from a field where typing goes (the shortcut stays out of it). */
export function isEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== "function") {
    return false;
  }
  return (
    el.isContentEditable ||
    el.closest("input, textarea, select, [contenteditable='true'], [role='textbox']") !== null
  );
}

/**
 * Opens the search dialog on Ctrl+K or ⌘K anywhere but inside a field where the user types
 * (the composer, a search field).
 */
export function useSearchShortcut(open: () => void) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() === "k" &&
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey &&
        !isEditable(event.target)
      ) {
        event.preventDefault();
        open();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);
}

/**
 * Conversation search (spec `web-shell` "Conversation search dialog"): recent conversations
 * before typing, then, from two characters, the conversations whose title or the user's own
 * questions match, with their project and a highlighted snippet. A combobox over a listbox: the
 * arrow keys move through results, Enter opens one, Escape closes and focus returns to where
 * it was. The result count is announced.
 */
export function SearchDialog(props: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  // Opened by a shortcut from anywhere: focus goes back where it was, not to a trigger.
  const opener = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (props.open) {
      opener.current = document.activeElement as HTMLElement | null;
    }
  }, [props.open]);
  return (
    <Dialog open={props.open} onOpenChange={(open) => (open ? undefined : props.onClose())}>
      <DialogContent
        onCloseAutoFocus={(event) => {
          if (opener.current?.isConnected && opener.current !== document.body) {
            event.preventDefault();
            opener.current.focus();
          }
        }}
        showCloseButton={false}
        className="top-[12vh] translate-y-0 gap-0 overflow-hidden rounded-xl p-0 sm:max-w-xl"
      >
        <DialogTitle className="sr-only">{t("search.title")}</DialogTitle>
        <DialogDescription className="sr-only">{t("search.hint")}</DialogDescription>
        {props.open ? <SearchBody onClose={props.onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function SearchBody(props: { onClose: () => void }) {
  const { t } = useTranslation();
  const id = useId();
  const [typed, setTyped] = useState("");
  const settled = useDebounced(typed.trim(), 200);
  const q = settled.length >= 2 ? settled : undefined;
  const searching = q !== undefined;
  const page = useConversationPage(searching ? { q, limit: RESULTS } : { limit: RECENT }, {
    staleTime: searching ? 0 : 30_000,
  });
  const items = page.data?.items ?? [];
  const [active, setActive] = useState(0);
  // A new list starts at its first result.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on a new query
  useEffect(() => {
    setActive(0);
  }, [q]);
  const current = Math.min(active, Math.max(items.length - 1, 0));
  const open = (item: ConversationListItem | undefined) => {
    if (item) {
      props.onClose();
      navigate(conversationPath(item));
    }
  };
  const optionId = (i: number) => `${id}-option-${i}`;
  const waiting = typed.trim().length >= 2 && (settled !== typed.trim() || page.isFetching);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2.5 border-b border-line px-4">
        <Search aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} />
        <input
          // biome-ignore lint/a11y/noAutofocus: the dialog exists to type a query.
          autoFocus
          role="combobox"
          aria-expanded={items.length > 0}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={items.length > 0 ? optionId(current) : undefined}
          aria-label={t("search.label")}
          placeholder={t("search.placeholder")}
          value={typed}
          maxLength={200}
          onChange={(e) => setTyped(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive(Math.min(current + 1, items.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive(Math.max(current - 1, 0));
            } else if (e.key === "Enter") {
              e.preventDefault();
              open(items[current]);
            }
          }}
          className="h-12 min-w-0 flex-1 bg-transparent text-[15px] text-fg outline-none placeholder:text-fg-muted"
        />
        <kbd className="hidden shrink-0 rounded-sm border border-line px-1.5 text-[11px] text-fg-muted sm:inline">
          Esc
        </kbd>
      </div>
      <div className="max-h-[min(60vh,28rem)] overflow-y-auto p-1.5">
        <p className="px-2.5 pt-1.5 pb-1 text-xs font-medium text-fg-muted">
          {searching ? t("search.results") : t("search.recent")}
        </p>
        {page.isPending ? (
          <div className="flex flex-col gap-2 px-2.5 py-2" aria-busy="true">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : page.isError ? (
          <p className="px-2.5 py-2 text-sm text-danger-fg">{page.error.message}</p>
        ) : items.length === 0 ? (
          <div className="flex flex-col gap-1 px-2.5 py-6 text-center">
            <p className="text-sm text-fg">{searching ? t("search.none") : t("search.noRecent")}</p>
            <p className="text-xs text-fg-muted">{t("search.hint")}</p>
          </div>
        ) : (
          <div
            id={`${id}-list`}
            role="listbox"
            aria-label={t("search.title")}
            className="flex flex-col"
          >
            {items.map((item, i) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox input owns the keys.
              <div
                key={item.id}
                id={optionId(i)}
                role="option"
                tabIndex={-1}
                aria-selected={i === current}
                onMouseMove={() => setActive(i)}
                onClick={() => open(item)}
                className={cn(
                  "flex cursor-pointer items-start gap-2.5 rounded-md px-2.5 py-2",
                  i === current && "bg-surface-hover",
                )}
              >
                <MessageCircle
                  aria-hidden="true"
                  className="mt-0.5 size-4 shrink-0 text-fg-muted"
                  strokeWidth={1.75}
                />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-medium text-fg">
                    {item.title ?? t("conversation.untitled")}
                  </span>
                  {item.snippet ? <Snippet snippet={item.snippet} /> : null}
                  <span className="inline-flex min-w-0 items-center gap-1 text-xs text-fg-muted">
                    <FolderClosed
                      aria-hidden="true"
                      className="size-3 shrink-0"
                      strokeWidth={1.75}
                    />
                    <span className="truncate">
                      {item.project?.name ?? t("conversationHeader.noProject")}
                    </span>
                    {item.archivedAt ? <span>· {t("conversationList.archived")}</span> : null}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <p role="status" className="sr-only">
        {searching && !waiting && page.isSuccess ? t("search.count", { count: items.length }) : ""}
      </p>
    </div>
  );
}
